
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageTransition from '@/components/layout/PageTransition';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  Plus, 
  Edit,
  Building,
  MapPin,
  Cpu
} from 'lucide-react';
import DeviceTable from '@/components/dashboard/DeviceTable';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { extendedDeviceData } from '@/data/mockHierarchy';
import { useHierarchy } from '@/contexts/HierarchyContext';

const ViewDevices = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hierarchyData } = useHierarchy();
  
  // Extract filter values from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const filterType = queryParams.get('filterType') || '';
  const filterId = queryParams.get('filterId') || '';
  
  // Set up necessary state variables
  const [hierarchyFilters, setHierarchyFilters] = useState<any>({});
  const [entityName, setEntityName] = useState('');
  const [formState, setFormState] = useState({
    name: '',
    description: '',
    parentId: '',
    accessLevel: 'restricted',
  });
  
  // Find the entity name based on filter type and ID
  useEffect(() => {
    let name = '';
    
    if (filterType === 'organization') {
      const org = hierarchyData.organizations.find(o => o.id === filterId);
      name = org?.name || 'Organization';
      setHierarchyFilters({ organization: filterId });
    } else if (filterType === 'location') {
      const loc = hierarchyData.locations.find(l => l.id === filterId);
      name = loc?.name || 'Location';
      setHierarchyFilters({ location: filterId });
    } else if (filterType === 'deviceType') {
      name = filterId || 'Device Type';
      setHierarchyFilters({ deviceType: filterId });
    }
    
    setEntityName(name);
  }, [filterType, filterId, hierarchyData]);
  
  // Count the devices that match our filters
  const deviceCount = extendedDeviceData.filter(device => {
    if (filterType === 'organization' && device.organizationId === filterId) return true;
    if (filterType === 'location' && device.locationId === filterId) return true;
    if (filterType === 'deviceType' && device.type === filterId) return true;
    return false;
  }).length;
  
  // Handle form submission for adding a new entity
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // In a real app, this would call an API to create the entity
    // For now, we'll just show a success message
    
    toast({
      title: `New ${filterType} created`,
      description: `${formState.name} has been created successfully.`,
    });
    
    // Reset the form
    setFormState({
      name: '',
      description: '',
      parentId: '',
      accessLevel: 'restricted',
    });
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setFormState(prev => ({ ...prev, [name]: value }));
  };
  
  const getEntityIcon = () => {
    if (filterType === 'organization') return <Building className="h-5 w-5" />;
    if (filterType === 'location') return <MapPin className="h-5 w-5" />;
    if (filterType === 'deviceType') return <Cpu className="h-5 w-5" />;
    return null;
  };
  
  // Determine parent dropdown options based on filter type
  const getParentOptions = () => {
    if (filterType === 'organization') {
      return null; // Organizations don't have parents in this system
    } else if (filterType === 'location') {
      return (
        <div className="grid gap-2">
          <Label htmlFor="parentId">Department</Label>
          <Select 
            value={formState.parentId} 
            onValueChange={(value) => handleSelectChange('parentId', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select department" />
            </SelectTrigger>
            <SelectContent>
              {hierarchyData.departments.map(dept => (
                <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    } else if (filterType === 'deviceType') {
      return null; // Device types don't have parents in this system
    }
    return null;
  };
  
  return (
    <PageTransition>
      <div className="section-padding">
        <div className="flex items-center gap-2 mb-6">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => navigate('/devices')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              {getEntityIcon()}
              {entityName}
            </h1>
            <p className="text-muted-foreground">
              Viewing {deviceCount} devices 
              {filterType === 'organization' && ' in this organization'}
              {filterType === 'location' && ' at this location'}
              {filterType === 'deviceType' && ' of this type'}
            </p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          {/* Add new entity button */}
          <Dialog>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add New {filterType === 'organization' ? 'Organization' : 
                          filterType === 'location' ? 'Location' : 
                          filterType === 'deviceType' ? 'Device Type' : 'Entity'}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  Create New {filterType === 'organization' ? 'Organization' : 
                              filterType === 'location' ? 'Location' : 
                              filterType === 'deviceType' ? 'Device Type' : 'Entity'}
                </DialogTitle>
                <DialogDescription>
                  Fill in the details to create a new {filterType}.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Name</Label>
                    <Input 
                      id="name" 
                      name="name" 
                      value={formState.name} 
                      onChange={handleInputChange} 
                      placeholder={`Enter ${filterType} name`} 
                      required 
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="description">Description</Label>
                    <Input 
                      id="description" 
                      name="description" 
                      value={formState.description} 
                      onChange={handleInputChange} 
                      placeholder={`Enter ${filterType} description`} 
                    />
                  </div>
                  
                  {getParentOptions()}
                  
                  <div className="grid gap-2">
                    <Label htmlFor="accessLevel">Security Classification</Label>
                    <Select 
                      value={formState.accessLevel} 
                      onValueChange={(value) => handleSelectChange('accessLevel', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select security level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="top-secret">Top Secret</SelectItem>
                        <SelectItem value="secret">Secret</SelectItem>
                        <SelectItem value="confidential">Confidential</SelectItem>
                        <SelectItem value="restricted">Restricted</SelectItem>
                        <SelectItem value="unclassified">Unclassified</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button type="submit">Create</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          
          {/* Edit current entity button - would link to a dedicated edit page */}
          <Button variant="outline" onClick={() => {
            toast({
              title: "Edit functionality",
              description: "In a complete application, this would open an edit form for this entity.",
            });
          }}>
            <Edit className="h-4 w-4 mr-2" />
            Edit {filterType === 'organization' ? 'Organization' : 
                  filterType === 'location' ? 'Location' : 
                  filterType === 'deviceType' ? 'Device Type' : 'Entity'}
          </Button>
          
          {/* Add device button */}
          <Button variant="outline" onClick={() => navigate('/add-device')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Device
          </Button>
        </div>
        
        {/* Device cards section */}
        <div className="grid gap-6 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Device List</CardTitle>
              <CardDescription>
                All devices {filterType === 'organization' ? 'in this organization' : 
                              filterType === 'location' ? 'at this location' : 
                              filterType === 'deviceType' ? 'of this type' : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DeviceTable 
                hierarchyFilters={hierarchyFilters}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </PageTransition>
  );
};

export default ViewDevices;
